import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {Box, Card, Checkbox, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel, Skeleton, Tooltip, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {DeleteOutlined, EditOutlined, FileDownloadOutlined} from "@mui/icons-material";
import classnames from "classnames";
import {Field} from "react-final-form";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import IconButton from "../../base/button/icon_button";
import Form, {MuiRFF, Yup} from "../../form";
import SaveOrRollback from "../../app-specific/save-or-rollback";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import useAccessLevels from "../../../hooks/use-access-levels";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {exportCSVFile} from "../../../../core/services/utils/utils";
import './index.scss';
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";

/**
 * Lists competitors with an inline create form and card-based edit (title + public checkbox).
 */
const ManageCompetitorsDialog = ({
                                     open,
                                     onClose,
                                     setOpen,
                                     onChanged,
                                 }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.manage-competitors'});
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canManage = operatorAccessLevels.packages.lookup.manageCompetitors;
    const canExport = operatorAccessLevels.packages.lookup.exportCompetitors;
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [competitors, setCompetitors] = useState([]);

    const busy = creating || exporting || competitors.some((entry) => entry.editing);

    /**
     * Resets local state when the dialog opens.
     */
    useLayoutEffect(() => {
        if (open) {
            setCompetitors([]);
            setLoading(false);
            setCreating(false);
            setExporting(false);
        }
    }, [open]);

    const closeDialog = useCallback(() => {
        if (busy)
            return;
        onClose?.();
        setOpen?.(false);
    }, [busy, onClose, setOpen]);

    /**
     * Loads the unpaginated competitor list.
     */
    const loadCompetitors = useCallback(async () => {
        setLoading(true);
        const response = await Api.listCompetitors();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setCompetitors((response.data ?? []).map((entry) => ({
                ...entry,
                inEditMode: false,
                editing: false,
            })));
        }
    }, [isMounted]);

    useEffect(() => {
        if (!open)
            return;
        void loadCompetitors();
    }, [loadCompetitors, open]);

    const createSchema = useMemo(() => Yup.object({
        title: Yup.string().trim().required(ValidateMessages.required),
    }), []);
    const createValidate = useMemo(() => MuiRFF.makeValidate(createSchema), [createSchema]);
    const createRequired = useMemo(() => MuiRFF.makeRequired(createSchema), [createSchema]);

    /**
     * Creates a competitor from the top form, then appends it to the list.
     */
    const createCompetitor = useCallback(async (values, form) => {
        setCreating(true);
        const response = await Api.addCompetitor({title: values.title.trim()});
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag) {
            setCompetitors((prev) => [
                ...prev,
                {
                    ...response.data,
                    inEditMode: false,
                    editing: false,
                },
            ]);
            form.change('title', '');
            form.resetFieldState('title');
            onChanged?.();
        }
    }, [isMounted, onChanged]);

    /**
     * Saves title and public flag for a competitor card.
     */
    const updateCompetitor = useCallback(async (competitor) => {
        setCompetitors((prev) => prev.map((entry) =>
            entry.id === competitor.id ? {...entry, editing: true} : entry,
        ));
        const response = await Api.updateCompetitor({
            id: competitor.id,
            title: competitor.title,
            isPublic: !!competitor.isPublic,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCompetitors((prev) => prev.map((entry) =>
                entry.id === competitor.id
                    ? {...entry, ...response.data, title: competitor.title, isPublic: !!competitor.isPublic, inEditMode: false, editing: false}
                    : entry,
            ));
            onChanged?.();
        } else {
            setCompetitors((prev) => prev.map((entry) =>
                entry.id === competitor.id ? {...entry, editing: false} : entry,
            ));
        }
    }, [isMounted, onChanged]);

    /**
     * Toggles inline edit mode on a competitor card.
     */
    const toggleInEditMode = useCallback((competitor, inEditMode = false) => {
        setCompetitors((prev) => prev.map((entry) =>
            entry.id === competitor.id ? {...entry, inEditMode} : entry,
        ));
    }, []);

    /**
     * Confirms delete with a replacement competitor, then calls Delete.
     */
    const removeCompetitor = useCallback((competitor) => {
        const replacements = competitors
            .filter((entry) => entry.id !== competitor.id)
            .map((entry) => ({
                id: entry.id,
                title: entry.title,
                data: entry,
            }));
        if (!replacements.length)
            return;
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            forceReplace: true,
            removedEntryTitle: competitor.title,
            data: replacements,
            onClose: async (confirmed, replacement) => {
                if (!confirmed)
                    return;
                const replacementId = replacement?.id ?? replacement?.data?.id;
                if (!replacementId)
                    return false;
                const response = await Api.deleteCompetitor(competitor.id, replacementId);
                if (!isMounted())
                    return false;
                if (response?.resultFlag) {
                    setCompetitors((prev) => prev.filter((entry) => entry.id !== competitor.id));
                    onChanged?.();
                }
                return response?.resultFlag ?? false;
            },
        }));
    }, [competitors, dispatch, isMounted, onChanged]);

    /**
     * Exports the full competitor list (single GET List call).
     */
    const exportCompetitors = useCallback(async () => {
        setExporting(true);
        window.partner?.openExportingDialog?.();
        const response = await Api.listCompetitors();
        if (!isMounted())
            return;
        setExporting(false);
        window.partner?.closeExportingDialog?.();
        if (!response?.resultFlag)
            return;
        exportCSVFile(
            (response.data ?? []).map((entry) => ({
                [t('export.title')]: entry.title,
                [t('export.isPublic')]: entry.isPublic ? t('yes') : t('no'),
            })),
            t('export.fileTitle'),
        );
    }, [isMounted, t]);

    const boxMixin = useCallback((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: 1,
        padding: 2,
        marginBlock: 2,
    }), []);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (nextOpen === false)
                    closeDialog();
            }}
            maxWidth={'sm'}
            fullWidth
            loading={busy}
            disableBackdropClick={busy}
            disableEscapeKeyDown={busy}
            className={'manage-competitors-dialog'}
        >
            <DialogTitle fontWeight={600}>
                {canManage ? t('titleManage') : t('titleView')}
            </DialogTitle>
            <DialogContent dividers>
                {
                    canManage &&
                    <Form
                        onSubmit={createCompetitor}
                        validate={createValidate}
                        initialValues={{title: ''}}
                    >
                        <Box sx={boxMixin}>
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                                <Typography variant={'subtitle2'} fontWeight={600}>
                                    {t('createTitle')}
                                </Typography>
                                {
                                    canExport &&
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        size={'small'}
                                        startIcon={<FileDownloadOutlined/>}
                                        onClick={exportCompetitors}
                                        disabled={busy || loading || competitors.length < 1}
                                    >
                                        {t('export.action')}
                                    </Button>
                                }
                            </Box>
                            <Grid2 container spacing={2} sx={{marginBlockStart: 1}}>
                                <Grid2 xs>
                                    <MuiRFF.TextField
                                        fullWidth
                                        size={'small'}
                                        name={'title'}
                                        label={t('title')}
                                        required={createRequired.title}
                                        disabled={creating || loading}
                                    />
                                </Grid2>
                                <Grid2 xs={'auto'} sx={{display: 'flex', alignItems: 'center'}}>
                                    <Button
                                        variant={'contained'}
                                        type={'submit'}
                                        color={'primary'}
                                        size={'medium'}
                                        disabled={creating || loading}
                                    >
                                        {creating ? t('creating') : t('create')}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Box>
                    </Form>
                }
                {
                    !canManage && canExport &&
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            size={'small'}
                            startIcon={<FileDownloadOutlined/>}
                            onClick={exportCompetitors}
                            disabled={busy || loading || competitors.length < 1}
                        >
                            {t('export.action')}
                        </Button>
                    </Box>
                }
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, marginBlockStart: 1}}>
                    {
                        loading &&
                        Array.from({length: 4}).map((_, key) => (
                            <Skeleton
                                key={key}
                                width={'100%'}
                                height={68}
                                sx={{transform: 'none', minHeight: 68}}
                            />
                        ))
                    }
                    {
                        !loading && competitors.map((competitor, _, array) => (
                            <CompetitorCard
                                key={competitor.id}
                                competitor={competitor}
                                update={updateCompetitor}
                                remove={removeCompetitor}
                                setInEditMode={toggleInEditMode}
                                canManage={canManage}
                                removable={canManage && array.length > 1}
                                busy={busy}
                            />
                        ))
                    }
                    {
                        !loading && competitors.length < 1 &&
                        <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                            textAlign={'center'}
                            sx={{marginBlock: 4, width: '100%'}}
                        >
                            {t('empty')}
                        </Typography>
                    }
                </Box>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                }}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={closeDialog}
                        disabled={busy}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('close')}
                    </Button>
                </Box>
            </DialogActions>
            {exporting && <CircularProgress sx={{display: 'none'}}/>}
        </Dialog>
    );
};

/**
 * Single competitor row: view, inline edit (title + public checkbox), delete.
 */
const CompetitorCard = ({
                            competitor,
                            update,
                            remove,
                            setInEditMode,
                            canManage,
                            removable,
                            busy,
                        }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.manage-competitors'});

    const schema = useMemo(() => Yup.object({
        title: Yup.string().trim().required(ValidateMessages.required),
        isPublic: Yup.boolean(),
    }), []);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        title: competitor?.title ?? '',
        isPublic: !!competitor?.isPublic,
    }), [competitor?.isPublic, competitor?.title]);

    /**
     * Saves title/public when they changed; otherwise just exits edit mode.
     */
    const onSubmit = useCallback(async (values) => {
        const title = values.title.trim();
        const isPublic = !!values.isPublic;
        if (title === competitor.title && isPublic === !!competitor.isPublic) {
            setInEditMode(competitor, false);
            return;
        }
        await update({...competitor, title, isPublic});
    }, [competitor, setInEditMode, update]);

    /**
     * Exits edit mode and restores the form values.
     */
    const onRollback = useCallback((form) => {
        setInEditMode(competitor, false);
        form.reset(initialValues);
    }, [competitor, initialValues, setInEditMode]);

    return (
        <Card
            variant={'outlined'}
            className={classnames('manage-competitors-dialog-card', {
                editing: competitor.inEditMode || competitor.editing,
            })}
            sx={{paddingInline: 2, paddingBlock: 1.5}}
        >
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
            >
                {({form}) => (
                    <Grid2 container spacing={1} alignItems={'center'}>
                        <Grid2 xs>
                            <MuiRFF.TextField
                                fullWidth
                                size={'small'}
                                name={'title'}
                                label={t('title')}
                                required={required.title}
                                className={classnames({'show-as-text': !competitor.inEditMode})}
                                disabled={competitor.editing || !canManage}
                            />
                        </Grid2>
                        <Grid2 xs={'auto'}>
                            {
                                competitor.inEditMode
                                    ? <Field name={'isPublic'} type={'checkbox'}>
                                        {({input}) => (
                                            <FormControlLabel
                                                sx={{margin: 0}}
                                                control={
                                                    <Checkbox
                                                        size={'small'}
                                                        checked={!!input.checked}
                                                        onChange={input.onChange}
                                                        disabled={competitor.editing}
                                                        icon={<CheckboxIcon/>}
                                                        checkedIcon={<ActiveCheckboxIcon/>}
                                                    />
                                                }
                                                label={t('isPublic')}
                                            />
                                        )}
                                    </Field>
                                    : <Chip
                                        size={'small'}
                                        label={competitor.isPublic ? t('public') : t('private')}
                                        color={competitor.isPublic ? 'success' : 'default'}
                                    />
                            }
                        </Grid2>
                        {
                            canManage &&
                            <Grid2 xs={'auto'}>
                                {
                                    !competitor.inEditMode &&
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <Tooltip title={t('edit')}>
                                            <span>
                                                <IconButton
                                                    color={'paper.primary'}
                                                    disabled={competitor.editing || busy}
                                                    onPressed={() => setInEditMode(competitor, true)}
                                                >
                                                    <EditOutlined/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {
                                            removable &&
                                            <Tooltip title={t('delete')}>
                                                <span>
                                                    <IconButton
                                                        color={'paper.error'}
                                                        disabled={competitor.editing || busy}
                                                        onPressed={() => remove(competitor)}
                                                    >
                                                        <DeleteOutlined/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        }
                                    </Box>
                                }
                                {
                                    competitor.inEditMode &&
                                    <SaveOrRollback
                                        column={false}
                                        show
                                        loading={competitor.editing}
                                        onRollback={() => onRollback(form)}
                                        onSave={() => form.submit()}
                                    />
                                }
                            </Grid2>
                        }
                    </Grid2>
                )}
            </Form>
        </Card>
    );
};

export default ManageCompetitorsDialog;
